import React from 'react';
import { Account } from '../../../sp-types/accountTypes';
import { GENERAL_ID } from '../../../components/Stores/exposureDataStore';
import { useDemandPositions } from '../../../hooks/useDemandPositions';
import { useSupplyPositions } from '../../../hooks/useSupplyPositions';
import { useFinancialPositions } from '../../../hooks/useFinancialPositions';
import type { HedgePosition } from '../../../sp-types/positions';
import {
  BufferMonthData,
  calculateBufferData,
  calculateCoverageData,
  calculateLogisticsData,
  CoverageMonthData,
  LogisticsMonthData,
  TimeGranularity,
} from '../components/StrategiesUtils';

import type { PolicyConfig } from '../../../hooks/useWatchRules';

const BUFFER_DEFAULT = 60;

interface UseStrategyDataParams {
  materialId: string | undefined;
  account: Account;
  activeSub: string;
  timeGranularity: TimeGranularity;
  logisticsTimeGranularity: TimeGranularity;
  terminals: number[];
  /** Watch-rule-derived policy config — single source of truth for strategy parameters. */
  policyConfig?: PolicyConfig;
}

export function useStrategyData({
  materialId,
  account,
  activeSub,
  timeGranularity,
  logisticsTimeGranularity,
  terminals,
  policyConfig,
}: UseStrategyDataParams) {
  // Fetch positions directly from Postgres — no adapters needed
  const { data: demandPositions = [], isLoading: isDemandLoading } = useDemandPositions();
  const { data: supplyPositions = [], isLoading: isSupplyLoading } = useSupplyPositions();
  const { data: hedgePositions, refetch: refetchHedgePositions } = useFinancialPositions();

  // Ensure hedgePositions is always a typed array
  const safeHedgePositions: HedgePosition[] = hedgePositions || [];

  // --- Buffer data ---
  const [bufferData, setBufferData] = React.useState<BufferMonthData[]>([]);

  React.useEffect(() => {
    if (materialId && supplyPositions.length > 0) {
      if (materialId === GENERAL_ID) {
        setBufferData([]);
        return;
      }

      const buffer = policyConfig?.buffer ?? BUFFER_DEFAULT;

      const calculatedData = calculateBufferData(
        materialId, supplyPositions, demandPositions, buffer, timeGranularity, terminals,
      );
      setBufferData(calculatedData);
    }
  }, [materialId, demandPositions, supplyPositions, account, timeGranularity, terminals, policyConfig]);

  // --- Logistics & Coverage data ---
  const [coverageData, setCoverageData] = React.useState<CoverageMonthData[]>([]);
  const [logisticsData, setLogisticsData] = React.useState<LogisticsMonthData[]>([]);

  // Clear data immediately when materialId changes to prevent stale data
  React.useEffect(() => {
    setCoverageData([]);
    setLogisticsData([]);
  }, [materialId]);

  React.useEffect(() => {
    if (materialId && (supplyPositions.length > 0 || demandPositions.length > 0)) {
      if (materialId === GENERAL_ID) return;

      const isCoverage = activeSub === 'coverage';
      const buffer = policyConfig?.buffer ?? BUFFER_DEFAULT;

      try {
        if (isCoverage) {
          const coverageDeltas = calculateCoverageData(
            materialId,
            supplyPositions,
            demandPositions,
            safeHedgePositions,
            policyConfig?.coverageTargets ?? [],
            buffer,
            logisticsTimeGranularity,
            terminals,
          );
          setCoverageData(coverageDeltas);
        } else {
          const data = calculateLogisticsData(
            materialId,
            supplyPositions,
            demandPositions,
            safeHedgePositions,
            false,
            policyConfig?.coverageTargets ?? [],
            policyConfig?.coverageIsPercent ?? false,
            policyConfig?.logisticsTargets ?? [],
            policyConfig?.logisticsIsPercent ?? false,
            buffer,
            logisticsTimeGranularity,
            terminals,
          );
          setLogisticsData(data);
        }
      } catch (error) {
        console.error('[Strategy] Calculation error:', error);
        if (isCoverage) {
          setCoverageData([]);
        } else {
          setLogisticsData([]);
        }
      }
    }
  }, [
    materialId, demandPositions, supplyPositions, safeHedgePositions,
    activeSub, logisticsTimeGranularity, account, account.formulasToAccount,
    terminals, policyConfig,
  ]);

  return {
    // Raw positions (used by HedgePriceManagement, Strategies.tsx render)
    supplyPositions,
    demandPositions,
    hedgePositions: safeHedgePositions,
    // Loading
    isDemandLoading,
    isSupplyLoading,
    // Calculated policy data
    bufferData,
    coverageData,
    logisticsData,
    // Helpers
    refetchHedgePositions,
    // Constants
    BUFFER_DEFAULT,
  };
}
